import React from "react";
import { map, meanBy, round } from "lodash";
import dayjs from "dayjs";
import {
  AssessmentEvaluation,
  Person,
} from "@/features/people/graphql/generated";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/shared/ui/HoverCard";
import { EvaluationScoreRow } from "@/shared/ui/EvaluationScoreRow";
import { Column, Row } from "@/shared/ui/Blocks";
import { SubtitleText } from "@/shared/ui/Typography";
import { AvatarWithName } from "@/shared/ui/Avatar";
import { cn } from "@/shared/utils/shadcn";
import { useFeatureFlag } from "@/shared/hooks/useFeatureFlag";
import { FeatureFlagKey } from "@/types/feature-flags";

export interface AssessmentEvaluationWithPerson extends AssessmentEvaluation {
  createdBy: Person;
}

interface PersonEvaluationsHoverOverProps {
  evaluations: AssessmentEvaluationWithPerson[];
  scoreRange?: number;
  title: string;
  variant?: "numeric" | "default";
  colorScheme?: "default" | "blue" | "white" | "blue-light";
}

export function EvaluationRow({
  evaluation,
  variant = "default",
  colorScheme = "default",
  scoreRange = 5,
}: {
  evaluation: AssessmentEvaluationWithPerson;
  variant?: "numeric" | "default";
  colorScheme?: "default" | "blue" | "white" | "blue-light";
  scoreRange?: number;
}) {
  return (
    <Row className="items-center justify-between space-x-4" key={evaluation.id}>
      {evaluation.createdBy && (
        <div>
          <AvatarWithName
            direction="row"
            avatar={evaluation.createdBy?.avatar || ""}
            name={evaluation.createdBy?.name || ""}
            initials={evaluation.createdBy?.initials || ""}
            isContact={!evaluation.createdBy?.id}
            description={dayjs(evaluation.created_at).format(
              "D MMM YYYY, h:mma"
            )}
          />
        </div>
      )}
      <Column className="items-center space-y-2">
        <EvaluationScoreRow
          score={evaluation.evaluation || 0}
          variant={variant}
          colorScheme={colorScheme}
        />
        {variant !== "numeric" && (
          <SubtitleText size="small" className="whitespace-nowrap ">
            {`${evaluation.evaluation || 0} out of ${scoreRange}`}
          </SubtitleText>
        )}
      </Column>
    </Row>
  );
}

function PersonEvaluationsHoverOver(props: PersonEvaluationsHoverOverProps) {
  const isFourPointScoring = useFeatureFlag(FeatureFlagKey.FOUR_POINT_SCORING);
  const {
    evaluations,
    scoreRange = isFourPointScoring ? 4 : 5,
    variant = "default",
    colorScheme = "default",
  } = props;

  const averageScore = round(meanBy(evaluations, "evaluation") || 0, 1);

  return (
    <HoverCard openDelay={100} closeDelay={100}>
      <HoverCardTrigger asChild>
        <EvaluationScoreRow
          score={averageScore}
          variant={variant}
          colorScheme={colorScheme}
        />
      </HoverCardTrigger>
      <HoverCardContent side="top" className="p-0">
        <Column
          className={cn("p-4 space-y-4", variant === "numeric" && "space-y-8")}
        >
          <Row className="items-center justify-between space-x-4">
            <h6 className="text-sm font-semibold text-grey-900 max-w-[240px] text-wrap leading-tight">
              {props.title}
            </h6>
            {evaluations?.length > 1 && (
              <Column className="items-center space-y-2">
                <EvaluationScoreRow
                  score={averageScore}
                  variant={variant}
                  colorScheme={colorScheme}
                />
                {variant !== "numeric" && (
                  <SubtitleText className="whitespace-nowrap text-xs">
                    {`${averageScore} out of ${scoreRange}`}
                  </SubtitleText>
                )}
              </Column>
            )}
          </Row>
          {map(evaluations, (evaluation) => (
            <EvaluationRow
              key={evaluation.id}
              evaluation={evaluation}
              variant={variant}
              colorScheme={colorScheme}
            />
          ))}
        </Column>
      </HoverCardContent>
    </HoverCard>
  );
}

export default PersonEvaluationsHoverOver;
